import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  useTheme,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import { useCookies } from 'react-cookie';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { sendCustomerOtp, verifyCustomerOtp } from '../../../services/request/CustomerAuth';
import { BackdropComponent } from '../../../shared-components/backdrop/Backdrop';
import { CUSTOMER_DASHBOARD } from '../../../services/constants/URIConstants';
import { Email, Lock } from '@mui/icons-material';

const CustomerLogin: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [cookies, setCookie] = useCookies(['auth_token', 'user_role']);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const emailFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    }),
    onSubmit: async (values) => {
      setOpen(true);
      try {
        const result = await sendCustomerOtp(values.email);
        setOpen(false);
        if (result && (result.code === 200 || result.statusCode === 200)) {
          setEmail(values.email);
          setOtpSent(true);
          setStep('otp');
          toast.success('OTP sent to your email!', { position: 'bottom-center' });
        } else {
          toast.error(result?.message || 'Failed to send OTP', { position: 'bottom-center' });
        }
      } catch (err: any) {
        setOpen(false);
        const errorMsg = err?.response?.data?.message || err?.message || 'Failed to send OTP';
        toast.error(errorMsg, { position: 'bottom-center' });
      }
    },
  });

  const otpFormik = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required('OTP is required')
        .matches(/^\d{6}$/, 'OTP must be 6 digits'),
    }),
    onSubmit: async (values) => {
      setOpen(true);
      try {
        const result = await verifyCustomerOtp(email, values.otp);
        setOpen(false);
        if (result && result.data && (result.code === 200 || result.statusCode === 200)) {
          const loginData = result.data;
          setCookie('auth_token', loginData.token, { path: '/' });
          setCookie('user_role', 'CUSTOMER', { path: '/' });
          toast.success('Login successful!', { position: 'bottom-center' });
          navigate(CUSTOMER_DASHBOARD);
        } else {
          toast.error(result?.message || 'Invalid OTP', { position: 'bottom-center' });
        }
      } catch (err: any) {
        setOpen(false);
        const errorMsg = err?.response?.data?.message || err?.message || 'Invalid OTP';
        toast.error(errorMsg, { position: 'bottom-center' });
      }
    },
  });

  const handleResendOtp = async () => {
    setOpen(true);
    try {
      const result = await sendCustomerOtp(email);
      setOpen(false);
      if (result && (result.code === 200 || result.statusCode === 200)) {
        toast.success('OTP resent to your email!', { position: 'bottom-center' });
      } else {
        toast.error(result?.message || 'Failed to resend OTP', { position: 'bottom-center' });
      }
    } catch (err: any) {
      setOpen(false);
      toast.error('Failed to resend OTP', { position: 'bottom-center' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <BackdropComponent open={open} />
      <ToastContainer />
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
              Customer Login
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step === 'email' ? 'Enter your email to receive OTP' : 'Enter the OTP sent to your email'}
            </Typography>
          </Box>

          {step === 'email' ? (
            <form onSubmit={emailFormik.handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={emailFormik.values.email}
                onChange={emailFormik.handleChange}
                onBlur={emailFormik.handleBlur}
                error={emailFormik.touched.email && Boolean(emailFormik.errors.email)}
                helperText={emailFormik.touched.email && emailFormik.errors.email}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={open}
                sx={{ py: 1.5, mb: 2 }}
              >
                {open ? <CircularProgress size={24} /> : 'Send OTP'}
              </Button>
            </form>
          ) : (
            <form onSubmit={otpFormik.handleSubmit}>
              <Alert severity="info" sx={{ mb: 3 }}>
                OTP sent to <strong>{email}</strong>
              </Alert>
              <TextField
                fullWidth
                label="Enter OTP"
                name="otp"
                type="text"
                value={otpFormik.values.otp}
                onChange={otpFormik.handleChange}
                onBlur={otpFormik.handleBlur}
                error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
                helperText={otpFormik.touched.otp && otpFormik.errors.otp || 'Enter the 6-digit code'}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 6 }}
                InputProps={{
                  startAdornment: <Lock sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={open}
                sx={{ py: 1.5, mb: 2 }}
              >
                {open ? <CircularProgress size={24} /> : 'Verify & Login'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResendOtp}
                disabled={open}
                sx={{ py: 1.5 }}
              >
                Resend OTP
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setStep('email');
                  setOtpSent(false);
                  otpFormik.resetForm();
                }}
                sx={{ mt: 1 }}
              >
                Change Email
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default CustomerLogin;
